/**
 * sessionRecovery — the ONE definition of "may this session be ended?"
 *
 * Why this module exists
 * ======================
 * Four independent places could destroy a session (UnifiedAuth0Provider's boot
 * verify, AuthContext's local-expiry check, and two sites in app/index.tsx),
 * and each decided for itself. None of them attempted a refresh first, and
 * `secureTokenStorage.clearAll()` deletes the Auth0 REFRESH token — so the
 * first actor to reach a clear destroyed the credential every other actor
 * needed. On a 7-day JWT that expired while the app was closed, the
 * pure-local check always won that race, which is why an expiry that should
 * have been invisible became a forced sign-out — weekly, for every user, by
 * construction. Measured in production 2026-08-14: sub=810c13d3,
 * age_s=605465 (7.0077 days), the entire screen-load fan-out 401ing.
 *
 * The rule, in one place so it cannot drift:
 *
 *   TERMINAL      — Auth0 positively refused the credential, or there is no
 *                   refresh token to try. Clearing is correct.
 *   INCONCLUSIVE  — everything else: offline, backend hiccup, SDK not wired
 *                   yet, or a failure we could not classify. KEEP the tokens.
 *
 * The asymmetry is the safety property. A wrong "inconclusive" costs one
 * retry; a wrong "terminal" costs the user their session AND deletes the
 * credential that would have restored it. When in doubt, keep.
 *
 * Pure decision logic (`isTerminalFailure`) is separated from the async
 * attempt so the rule can be unit-tested exhaustively without mocking Auth0 —
 * the same shape as services/offlineRetryPolicy.ts, whose data-destroying
 * branch went unnoticed for exactly as long as it was untestable.
 */

import { getTokenManager, type RefreshFailure } from './tokenManager';

/**
 * The only two failures that justify ending a session.
 *
 * ⚠️ Adding to this set makes sign-outs MORE likely — the direction that hurt
 * users. Any addition needs the same scrutiny as a data-deleting branch.
 */
const TERMINAL_FAILURES: readonly RefreshFailure[] = ['rejected', 'no_refresh_token'];

export function isTerminalFailure(failure: RefreshFailure): boolean {
  return TERMINAL_FAILURES.includes(failure);
}

export type SessionRecovery =
  /** A new Cedar JWT is in hand and persisted. */
  | { kind: 'recovered'; token: string }
  /** The credential is genuinely dead. Clearing is correct. */
  | { kind: 'terminal'; failure: RefreshFailure }
  /** We could not tell. Keep the tokens and let a later attempt heal it. */
  | { kind: 'inconclusive'; failure: RefreshFailure };

/**
 * Try to replace an expired/rejected Cedar JWT, and say plainly whether the
 * caller is allowed to end the session.
 *
 * Never throws: TokenManager.refreshDetailed() is total by construction, and
 * an unexpected throw here would land in a boot path where the failure mode is
 * a white screen. Anything unexpected degrades to `inconclusive`, which is the
 * non-destructive answer.
 */
export async function attemptSessionRecovery(): Promise<SessionRecovery> {
  try {
    const { token, failure } = await getTokenManager().refreshDetailed();
    if (token) return { kind: 'recovered', token };
    return isTerminalFailure(failure)
      ? { kind: 'terminal', failure }
      : { kind: 'inconclusive', failure };
  } catch {
    return { kind: 'inconclusive', failure: 'network' };
  }
}
